import { fetchDocScannerExtraction } from "../../../utilis/docScannerClient";
import {
  getLlmExtractionFields,
  normalizeExtractedJsonForDocument,
} from "../../documentTypes";
import { linesToRawText } from "../paddle/paddleTextAdapter";
import { createOcrJob } from "./ocrJobApi";
import { pollOcrJob } from "./ocrJobPoller";

/**
 * Run existing OCR once, then async LLM extraction via backend job.
 *
 * @param {File} file
 * @param {string} documentType
 * @param {Function} [onProgress]
 * @param {{ signal?: AbortSignal, onPartial?: Function, onStatus?: Function }} [options]
 */
export const extractViaLlmPipeline = async (
  file,
  documentType,
  onProgress,
  options = {},
) => {
  const ocrResponse = await fetchDocScannerExtraction(file, {
    signal: options.signal,
    documentType,
    onProgress: (partial) => {
      onProgress?.(partial);
      options.onPartial?.({
        raw_text: partial?.rawText || "",
        cleaned_text: "",
        extracted_json: "{}",
      });
    },
  });

  const result = ocrResponse?.result || {};
  const ocrLines = result.ocrLines || [];
  const rawText =
    String(result.rawText || "").trim() ||
    (ocrLines.length ? linesToRawText(ocrLines) : "");

  if (!rawText) {
    throw new Error("Could not read text from this document.");
  }

  const fields = getLlmExtractionFields(documentType);
  if (!fields.length) {
    throw new Error("Unsupported document type for LLM extraction.");
  }

  options.onStatus?.("submitting_llm_job");
  console.info("[LLM] Submitting raw OCR to /ocr/jobs", {
    documentType,
    fieldCount: fields.length,
    rawTextLength: rawText.length,
  });

  const created = await createOcrJob(documentType, { rawText }, fields);
  console.info("[LLM] Job created", { jobId: created.id, status: created.status });
  options.onStatus?.("pending", created);

  const completed = await pollOcrJob(created.id, {
    signal: options.signal,
    onStatus: (status, payload) => {
      if (status === "ai_processing") {
        options.onStatus?.("ai_processing", payload);
      } else if (status === "stale") {
        options.onStatus?.("stale", payload);
      } else {
        options.onStatus?.(status, payload);
      }
    },
  });

  const extracted = normalizeExtractedJsonForDocument(
    documentType,
    completed?.extracted_json || {},
    rawText,
  );
  console.info("[LLM] Extraction completed", { jobId: created.id, extracted });

  return {
    raw_text: rawText,
    cleaned_text: "",
    extracted_json: JSON.stringify(extracted),
  };
};
